import {
  capitalizeFirst,
  formatText,
  formatWeightLb,
  getEquipmentTypesDisplay as getEquipmentTypesAsSingleString,
  getServiceTypeDisplayLabel,
  ISOStringToShorthandDate,
  toFullReadableAddress,
} from "utils";

import type { CompanyProfile } from "types/company";

function getSupplierShipmentDetailsSection(
  supplierShipment: SupplierShipment,
  includeCarrierInfo: boolean,
  companyProfile?: CompanyProfile | null,
) {
  const shipmentInfo = supplierShipment?.info;
  const items: { label: string; value: string }[] = [];

  // Add carrier information if requested and available
  if (includeCarrierInfo && supplierShipment?.booked_carrier) {
    if (companyProfile?.company?.name) {
      items.push({
        label: "Carrier Company",
        value: companyProfile.company.name,
      });
    }

    if (supplierShipment.booked_carrier.user) {
      const carrierUser = supplierShipment.booked_carrier.user;
      items.push({
        label: "Carrier Contact",
        value: `${carrierUser.first_name} ${carrierUser.last_name}`,
      });
    }
  }

  // Supplier company information
  items.push({
    label: "Supplier Company",
    value: supplierShipment.supplier_company?.name || "",
  });

  // Supplier contact information
  if (supplierShipment.supplier?.user) {
    items.push({
      label: "Supplier Contact",
      value: `${supplierShipment.supplier.user.first_name} ${supplierShipment.supplier.user.last_name}`,
    });
  }

  // Basic shipment details
  items.push(
    {
      label: "Commodity",
      value: shipmentInfo?.commodity_freetext
        ? capitalizeFirst(formatText(shipmentInfo.commodity_freetext))
        : "General Freight",
    },
    {
      label: "Weight",
      value:
        shipmentInfo?.weight_lb != null
          ? formatWeightLb(shipmentInfo.weight_lb, { includeKg: false })
          : "",
    },
    {
      label: "Service",
      value: getServiceTypeDisplayLabel(shipmentInfo?.service ?? ""),
    },
    {
      label: "Equipment Type",
      value:
        supplierShipment.equipments?.length > 0
          ? getEquipmentTypesAsSingleString(
              supplierShipment.equipments.map((e) => e.type),
            )
          : "",
    },
    {
      label: "Special Requirements",
      value: shipmentInfo?.special_requirements || "",
    },
    {
      label: "Additional Details",
      value: shipmentInfo?.additional_details || "",
    },
  );

  // Footage if available
  if (shipmentInfo?.footage) {
    items.push({
      label: "Footage",
      value: `${shipmentInfo.footage} ft`,
    });
  }

  return {
    id: "supplier-shipment-details",
    title: "Supplier Shipment Details",
    items,
  };
}

function getSupplierShipmentRouteSection(
  supplierShipment: SupplierShipment,
  route: Route | null,
  etas?: RoutePointETA[] | null,
) {
  if (!route || route.route_points.length === 0) {
    // Fallback to first_point and last_point if route is not available
    const items: { label: string; value: string }[] = [];

    if (supplierShipment.first_point?.point_location) {
      items.push({
        label: "Origin",
        value:
          toFullReadableAddress(supplierShipment.first_point.point_location) ||
          "",
      });
    }

    if (supplierShipment.last_point?.point_location) {
      items.push({
        label: "Destination",
        value:
          toFullReadableAddress(supplierShipment.last_point.point_location) ||
          "",
      });
    }

    return {
      id: "supplier-shipment-route",
      title: "Supplier Shipment Route",
      items,
    };
  }

  const items: { label: string; value: string }[] = [];
  const sortedPoints = route.route_points.sort(
    (a: RoutePoint, b: RoutePoint) => a.point_number - b.point_number,
  );
  const originPoint = sortedPoints.find(
    (p: RoutePoint) => p.stop_type === "ORIGIN",
  );
  const destinationPoint = sortedPoints.find(
    (p: RoutePoint) => p.stop_type === "DESTINATION",
  );
  const middlePoints = sortedPoints.filter(
    (p: RoutePoint) => p.stop_type === "MID",
  );

  if (originPoint) {
    items.push({
      label: "Origin",
      value: toFullReadableAddress(originPoint?.point_location) || "",
    });

    // Add origin ETA if available
    if (etas && etas.length > 0) {
      const originETA = etas.find((eta) => eta.point.id === originPoint.id);
      if (originETA?.eta) {
        items.push({
          label: "Origin Date ETA (dd/mm/yyyy)",
          value: ISOStringToShorthandDate(originETA.eta),
        });
      }
    }
  }

  // Include all middle points if present
  if (middlePoints && middlePoints.length > 0) {
    middlePoints.forEach((point: RoutePoint) => {
      items.push({
        label: `Stop ${point.point_number - 1} Location`,
        value: toFullReadableAddress(point.point_location) || "",
      });
    });
  }

  if (destinationPoint) {
    items.push({
      label: "Destination",
      value: toFullReadableAddress(destinationPoint?.point_location) || "",
    });

    // Add destination ETA if available
    if (etas && etas.length > 0) {
      const destinationETA = etas.find(
        (eta) => eta.point.id === destinationPoint.id,
      );
      if (destinationETA?.eta) {
        items.push({
          label: "Destination Date ETA (dd/mm/yyyy)",
          value: ISOStringToShorthandDate(destinationETA.eta),
        });
      }
    }
  }

  return {
    id: "supplier-shipment-route",
    title: "Supplier Shipment Route",
    items,
  };
}

export function getSupplierShipmentExportPDFRenderConfig(
  supplierShipment: SupplierShipment,
  route: Route | null,
  includeCarrierInfo = false,
  companyProfile?: CompanyProfile | null,
  etas?: RoutePointETA[] | null,
) {
  const shipmentDetails = getSupplierShipmentDetailsSection(
    supplierShipment,
    includeCarrierInfo,
    companyProfile,
  );
  const routeDetails = getSupplierShipmentRouteSection(
    supplierShipment,
    route,
    etas,
  );
  return [shipmentDetails, routeDetails];
}
